import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { Anchor, Box, Button, Flex, LoadingOverlay, Stack } from '@mantine/core';

import useSSOAuth, { hasSSOCallbackParams, isSSOLogoutCallback } from '@/hooks/useSSOAuth.ts';

import { selectAccessToken } from '@/store/user-process/user-selectors.ts';

import { LoginForm } from '@/features/Auth/LoginForm.tsx';

import { getAuthProvider, isKeycloakConfigured } from '@/shared/helpers/keycloak.ts';

import { useAppSelector } from '@/hooks';

const Login = () => {
	const accessToken = useAppSelector(selectAccessToken);
	const [isAltAuthFormVisible, setAltAuthFormVisible] = useState(false);
	const [isProcessingSSO, setProcessingSSO] = useState(() => hasSSOCallbackParams());
	const { loginWithSSO, completeSSOLoginIfNeeded, completeSSOLogoutIfNeeded, isLoading } =
		useSSOAuth();

	useEffect(() => {
		// Флаги читаются до того, как обработчики почистят query-параметры
		const isLogoutCallback = isSSOLogoutCallback();

		completeSSOLogoutIfNeeded();

		if (hasSSOCallbackParams()) {
			void completeSSOLoginIfNeeded().finally(() => setProcessingSSO(false));
			return;
		}

		// Авто-SSO не запускается после явного выхода (иначе вечный цикл)
		if (
			isKeycloakConfigured() &&
			!accessToken &&
			!isLogoutCallback &&
			getAuthProvider() === 'sso'
		) {
			setProcessingSSO(true);
			void loginWithSSO().finally(() => setProcessingSSO(false));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (isProcessingSSO) {
		return (
			<Box pos="relative" h="100%">
				<LoadingOverlay visible zIndex={1000} />
			</Box>
		);
	}

	if (accessToken && !isSSOLogoutCallback()) {
		return <Navigate to="/" replace />;
	}

	return (
		<Flex align="center" justify="center" h="100%" px="lg" w="100%">
			{isAltAuthFormVisible ? (
				<LoginForm onBack={() => setAltAuthFormVisible(false)} />
			) : (
				<Stack align="center" gap="sm">
					<Button
						loading={isLoading}
						onClick={() => {
							setProcessingSSO(true);
							void loginWithSSO().finally(() => setProcessingSSO(false));
						}}
						disabled={!isKeycloakConfigured()}
					>
						Войти с SSO
					</Button>
					<Anchor component="button" type="button" onClick={() => setAltAuthFormVisible(true)}>
						Другой способ входа
					</Anchor>
				</Stack>
			)}
		</Flex>
	);
};

export default Login;
